/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import cn from "clsx";
import { Backdrop } from "@mui/material";
import { Configure, InstantSearch } from "react-instantsearch";
import { FaWrapper } from "../fa-wrapper";
import { QuickSearchInput } from "../input/quick-search-input";
import { useAlgolia } from "../hooks/use-algolia";
import { useQuickSearch } from "./use-quick-search";
import { CompactResults } from "./results/compact-results";

const QuickSearch = () => {

    const { algoliaClient, routing, searchIndex } = useAlgolia();
    const {
        backDropId,
        closeOnAnchorClick,
        closeOnOverlayFocus,
        inputRef,
        onInputFocus,
        showResults,
        showSearchInput,
        toggleSearchInput,
    } = useQuickSearch();

    const cssQuickSearch = cn("quick-search-container", { "quick-search--visible": showSearchInput });
    const showInverted = !showSearchInput;

    const onBlur = (event) => {
        // only if blur is moving outside of compact-results-container
        if (!event.currentTarget.contains(event.relatedTarget)) {
            closeOnOverlayFocus(event);
        }
    };

    return (
        <InstantSearch
            searchClient={algoliaClient}
            indexName={searchIndex}
            routing={routing}
            insights
            future={{
                preserveSharedStateOnUnmount: true,
            }}
        >
            <Configure
                hitsPerPage={5}
                filters="is_document:false"
                // this prevents algolia from forcing these values and sending another request
                highlightPreTag="__ais-highlight__"
                highlightPostTag="__/ais-highlight__"
            />
            <div onClick={closeOnAnchorClick}>
                <div id="search-container" className={cssQuickSearch}>
                    {/*  shown on bigger screens  */}
                    <QuickSearchInput
                        ref={inputRef}
                        onChange={onInputFocus}
                        onFocus={onInputFocus}
                        navigateOnSubmit
                        autoSearch
                    >
                        <FaWrapper icon="fa-regular fa-magnifying-glass" fontSize="18px" show />
                    </QuickSearchInput>

                    {/*  shown on mobile */}
                    <button
                        type="button"
                        className={`quick-search-submit ` + (showSearchInput ? "collapse-submit" : "")}
                        aria-label="Toggle the search button"
                        onClick={toggleSearchInput}
                    >
                        <FaWrapper
                            icon="fa-regular fa-magnifying-glass"
                            fontSize="18px"
                            show={showInverted}
                        />

                        <FaWrapper
                            customClassName={`mobile-input-search-submit ${showSearchInput ? "show" : "hidden"}`}
                            icon="fa-regular fa-arrow-left-from-line"
                            fontSize="18px"
                            show={showSearchInput}
                        />
                    </button>

                    {showResults && (
                        <div style={{ position: "relative" }}>
                            <CompactResults onBlur={onBlur}/>
                        </div>
                    )}
                </div>

                <Backdrop
                    id={backDropId}
                    sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={showResults}
                    transitionDuration={0}
                    onClick={closeOnOverlayFocus}
                />
            </div>
        </InstantSearch>
    );
};

export { QuickSearch };
